// Motor de las notificaciones en vivo. Se arranca una vez desde main.js y
// sigue a la sesión: con usuario logueado se suscribe a su canal privado de
// Pusher (private-usuario.{id}) y, por cada aviso nuevo:
//  - lo agrega a la campana (store `notificaciones`);
//  - si la pestaña está a la vista: cartel abajo a la derecha + sonido;
//  - si no hay ninguna pestaña a la vista: aviso del sistema operativo (si el
//    usuario lo activó) + sonido;
//  - con varias pestañas abiertas suena UNA sola vez;
//  - si el mensaje es de la conversación que está mirando, no avisa: lo da
//    por leído.
// También pone el contador en el título de la pestaña: "(3) House & Flats".

import { getPusher, subscribeToChannel, unsubscribeFromChannel } from "@/Utils/PusherUtils";
import { textoNotificacion } from "@/components/Notificaciones/textos";

const LS_AVISO = "hyf_notif_aviso";

let store = null;
let router = null;
let i18n = null;
let canal = null;
let audio = null;

// ─── Sonido ──────────────────────────────────────────────────────────────
// Se genera con Web Audio (dos notas cortas): no hace falta ningún archivo.
// Los navegadores no dejan sonar nada hasta el primer clic o tecla en la
// página, así que el contexto se crea recién ahí.

function desbloquearAudio() {
    try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        if (!audio) audio = new Ctx();
        if (audio.state === "suspended") audio.resume();
    } catch (e) { /* sin audio */ }
}

export function sonar() {
    if (!audio || audio.state !== "running") return;
    try {
        const t0 = audio.currentTime + 0.02;
        [[880, 0], [1318.5, 0.12]].forEach(([f, dt]) => {
            const o = audio.createOscillator();
            const g = audio.createGain();
            o.type = "sine";
            o.frequency.value = f;
            g.gain.setValueAtTime(0.0001, t0 + dt);
            g.gain.exponentialRampToValueAtTime(0.18, t0 + dt + 0.02);
            g.gain.exponentialRampToValueAtTime(0.0001, t0 + dt + 0.32);
            o.connect(g).connect(audio.destination);
            o.start(t0 + dt);
            o.stop(t0 + dt + 0.35);
        });
    } catch (e) { /* sin audio */ }
}

// ─── Una sola vez entre pestañas ─────────────────────────────────────────
// Todas las pestañas reciben el mismo evento de Pusher. La que está a la vista
// lo reclama al instante; las de fondo esperan un poco y solo avisan si nadie
// lo reclamó (= no hay ninguna pestaña a la vista).

function reclamado(clave) {
    try { return localStorage.getItem(LS_AVISO) === clave; } catch (e) { return false; }
}
function reclamar(clave) {
    try { localStorage.setItem(LS_AVISO, clave); } catch (e) { /* modo privado */ }
}

// ─── Título de la pestaña ────────────────────────────────────────────────

const PREFIJO = /^\(\d+\+?\)\s/;
let poniendoTitulo = false;

function aplicarTitulo() {
    if (poniendoTitulo) return;
    const n = store ? store.state.notificaciones.noLeidas : 0;
    const base = document.title.replace(PREFIJO, "");
    const nuevo = n > 0 ? `(${n > 99 ? "99+" : n}) ${base}` : base;
    if (nuevo !== document.title) {
        poniendoTitulo = true;
        document.title = nuevo;
        poniendoTitulo = false;
    }
}

function vigilarTitulo() {
    // Si una página cambia el título, se le vuelve a poner el contador.
    const el = document.querySelector("title");
    if (el && typeof MutationObserver !== "undefined") {
        new MutationObserver(() => aplicarTitulo()).observe(el, { childList: true, characterData: true, subtree: true });
    }
}

// ─── Aviso del sistema operativo ─────────────────────────────────────────

export function permisoEscritorio() {
    if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
    return Notification.permission; // default | granted | denied
}

export async function pedirPermisoEscritorio() {
    if (permisoEscritorio() === "unsupported") return "unsupported";
    try {
        const r = await Notification.requestPermission();
        return r;
    } catch (e) {
        return permisoEscritorio();
    }
}

function avisoEscritorio(n) {
    if (!store.state.notificaciones.escritorio || permisoEscritorio() !== "granted") return false;
    try {
        const { titulo, cuerpo } = textoNotificacion(n, i18n);
        const aviso = new Notification(titulo, {
            body: cuerpo,
            icon: "/favicon.ico",
            tag: `hyf-${n.id}`,
            renotify: true,
        });
        aviso.onclick = () => {
            window.focus();
            abrir(n);
            aviso.close();
        };
        return true;
    } catch (e) {
        return false;
    }
}

// ─── Abrir una notificación (campana, cartel o aviso del sistema) ────────

let secAviso = 0;
let ultimoAvisoLocal = 0;

/**
 * Aviso de la propia página (no viene de la API): "Cambios guardados",
 * "No se pudo guardar"… Sale con el mismo cartel que las notificaciones.
 * No va a la campana ni suena. Sobrevive al cambio de ruta (por ejemplo,
 * guardar y volver a Mis espacios).
 *
 * @param {{ tipo?: 'ok'|'error'|'info', titulo: string, cuerpo?: string,
 *           accion?: { label: string, fn: Function }, duracion?: number }} aviso
 * @returns {string} id (para quitarlo con quitarAviso)
 */
export function avisar({ tipo = "ok", titulo, cuerpo = "", accion = null, duracion = null } = {}) {
    const id = `local-${Date.now()}-${++secAviso}`;
    ultimoAvisoLocal = Date.now();
    if (store) {
        store.commit("notificaciones/agregarToast", { id, local: true, tipo, titulo, cuerpo, accion, duracion, cantidad: 1 });
        // Todos los avisos suenan, igual que las notificaciones (salvo con el sonido apagado en la campana).
        if (!store.state.notificaciones.silenciado) sonar();
    }
    return id;
}

export function quitarAviso(id) {
    if (store) store.commit("notificaciones/quitarToast", id);
}

/**
 * Las pantallas viejas avisan con vue-toasted ($toasted.show/success/error/info).
 * Se redirigen a avisar() para que TODOS los avisos del sitio salgan con el
 * mismo cartel. Devuelve un objeto con goAway() / text() como vue-toasted.
 * Se llama desde main.js después de iniciarNotificaciones().
 */
export function conectarToasted(Vue) {
    const t = Vue.prototype.$toasted;
    if (!t || t.__hyf) return;
    const tipoDe = (o, msg) => {
        if (o.type === "error" || /\berror\b/.test(o.className || "")) return "error";
        if (o.type === "success") return "ok";
        if (o.type === "info") return "info";
        return /^(error|no se pudo|no fue posible)/i.test(msg) ? "error" : "info";
    };
    const mostrar = (msg, o = {}, forzado = null) => {
        const texto = String(msg == null ? "" : msg).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
        const accion = [].concat(o.action || [])[0];
        let id = null;
        const obj = { goAway() { quitarAviso(id); }, text() { return obj; }, el: null };
        id = avisar({
            tipo: forzado || tipoDe(o, texto),
            titulo: texto,
            duracion: o.duration || null,
            accion: accion && accion.text ? { label: accion.text, fn: () => { if (accion.onClick) accion.onClick(new Event("click"), obj); } } : null,
        });
        return obj;
    };
    t.show = (m, o) => mostrar(m, o);
    t.success = (m, o) => mostrar(m, o, "ok");
    t.error = (m, o) => mostrar(m, o, "error");
    t.info = (m, o) => mostrar(m, o, "info");
    t.__hyf = true;
}

export function abrir(n) {
    if (!n) return;
    store.commit("notificaciones/quitarToast", n.id);
    if (n.local) return;
    store.dispatch("notificaciones/leer", n.id);
    if (n.enlace && router) {
        const actual = router.currentRoute.fullPath;
        if (actual !== n.enlace) router.push(n.enlace).catch(() => {});
    }
}

// ─── Eventos de Pusher ───────────────────────────────────────────────────

// Avisos causados por el mismo usuario (la API manda `por` = quién lo hizo)
// y que la pantalla ya mostró con avisar() hace un momento: entran callados a
// la campana. Si la pantalla no mostró nada (p. ej. el BackOffice), salen con
// cartel y sonido como cualquier notificación.
function hechoPorMi(n) {
    const yo = Number(localStorage.getItem("idusuario"));
    const propio = !!yo && !!n.datos && Number(n.datos.por) === yo;
    return propio && Date.now() - ultimoAvisoLocal < 20000;
}

function mirandoConversacion(n) {
    return n.tipo === "mensaje"
        && !document.hidden
        && Number(store.state.notificaciones.conversacionAbierta) === Number(n.datos && n.datos.conversacion_id);
}

function alRecibir(payload) {
    const n = payload && payload.notificacion;
    if (!n) return;
    store.commit("notificaciones/recibir", { notificacion: n, noLeidas: payload.no_leidas });

    // Para que Mensajes actualice la lista (último mensaje, no leído…).
    try { window.dispatchEvent(new CustomEvent("hyf:notificacion", { detail: n })); } catch (e) { /* IE */ }

    if (mirandoConversacion(n)) {
        reclamar(`${n.id}:${n.cantidad}`);
        store.dispatch("notificaciones/leerEntidad", { entidad: "conversacion", id: n.datos.conversacion_id });
        return;
    }

    // Algo que hizo el mismo usuario (crear / editar / eliminar su alojamiento):
    // ya vio el aviso en la pantalla, así que solo queda en la campana, sin
    // cartel, sin sonido y sin aviso del sistema.
    if (hechoPorMi(n)) {
        reclamar(`${n.id}:${n.cantidad}`);
        return;
    }

    const clave = `${n.id}:${n.cantidad}`;
    const silencio = store.state.notificaciones.silenciado;

    if (!document.hidden) {
        // Cartel en toda pestaña a la vista; el sonido, una sola vez: la
        // pestaña con el foco lo reclama al instante, otra visible (dos
        // ventanas lado a lado) espera un poco por si la primera ya sonó.
        store.commit("notificaciones/agregarToast", n);
        const conFoco = typeof document.hasFocus === "function" ? document.hasFocus() : true;
        setTimeout(() => {
            if (reclamado(clave)) return;
            reclamar(clave);
            if (!silencio) sonar();
        }, conFoco ? 0 : 150 + Math.random() * 100);
        return;
    }

    // Pestaña de fondo: espera (con algo de azar para no pisarse con otras).
    setTimeout(() => {
        if (reclamado(clave)) return;
        reclamar(clave);
        avisoEscritorio(n);
        if (!silencio) sonar();
    }, 350 + Math.random() * 400);
}

function alLeer(payload) {
    if (!payload) return;
    store.commit("notificaciones/marcarLeidas", {
        ids: payload.ids || [],
        todas: !!payload.todas,
        noLeidas: payload.no_leidas,
    });
}

function conectar(idusuario) {
    const nuevo = idusuario ? `private-usuario.${idusuario}` : null;
    if (nuevo === canal) return;

    if (canal) unsubscribeFromChannel(canal);
    canal = nuevo;
    store.commit("notificaciones/reset");
    if (!canal) return;

    store.dispatch("notificaciones/cargar");
    const ch = subscribeToChannel(canal, "notificacion", alRecibir);
    ch.bind("notificacion.leida", alLeer);
    ch.bind("pusher:subscription_error", (e) => console.warn("[notificaciones] no se pudo suscribir", e));
}

/**
 * @param {{ store, router, i18n }} deps
 */
export function iniciarNotificaciones(deps) {
    if (store || typeof window === "undefined" || window.__PRERENDER_INJECTED) return;
    store = deps.store;
    router = deps.router;
    i18n = deps.i18n;

    ["pointerdown", "keydown", "touchstart"].forEach((ev) => window.addEventListener(ev, desbloquearAudio, { passive: true }));

    store.watch(
        (s) => (s.token && s.user && s.user.idusuario ? String(s.user.idusuario) : null),
        (id) => conectar(id),
        { immediate: true }
    );
    store.watch((s) => s.notificaciones.noLeidas, aplicarTitulo, { immediate: true });
    vigilarTitulo();

    // Al volver la conexión (se cortó internet, la PC se durmió…) se recarga
    // la lista: los avisos de mientras tanto no llegaron por Pusher.
    try {
        let yaConectado = false;
        getPusher().connection.bind("connected", () => {
            if (yaConectado && canal) store.dispatch("notificaciones/cargar");
            yaConectado = true;
        });
    } catch (e) { /* sin Pusher */ }
    document.addEventListener("visibilitychange", () => {
        // Volvió a la pestaña con la conversación abierta: lo pendiente queda leído.
        const c = store.state.notificaciones.conversacionAbierta;
        if (!document.hidden && c) store.dispatch("notificaciones/leerEntidad", { entidad: "conversacion", id: c });
    });
}
